import { type Frames, type Plan, BASE_FPS } from "./encode";

/**
 * Konkord: the part of the video kept, in fractions of its size (0 to 1)
 */
export interface Crop {
  x: number;
  y: number;
  width: number;
  height: number;
}

/**
 * The biggest box of the given ratio that fits the video, shrunk by the
 * zoom and kept inside the video around the chosen centre
 */
export function cropBox(
  videoWidth: number,
  videoHeight: number,
  ratio: number,
  zoom: number,
  centerX: number,
  centerY: number,
): Crop {
  const videoRatio = videoWidth / videoHeight;
  let width = 1;
  let height = 1;
  if (videoRatio > ratio) width = ratio / videoRatio;
  else height = videoRatio / ratio;
  width /= zoom;
  height /= zoom;
  const x = Math.min(1 - width, Math.max(0, centerX - width / 2));
  const y = Math.min(1 - height, Math.max(0, centerY - height / 2));
  return { x, y, width, height };
}

/**
 * Move to a time and wait until that frame is really there: "seeked" alone
 * can come before the new frame is decoded, and drawing then repeats the
 * old one. The frame callback fires once the new frame is ready.
 */
function seek(video: HTMLVideoElement, time: number) {
  return new Promise<void>((resolve) => {
    if (Math.abs(video.currentTime - time) < 0.001) return resolve();
    let done = false;
    const finish = () => {
      if (done) return;
      done = true;
      resolve();
    };
    video.requestVideoFrameCallback(finish);
    // in case no frame callback comes (hidden page): a little after seeking
    video.addEventListener("seeked", () => setTimeout(finish, 250), {
      once: true,
    });
    video.currentTime = time;
  });
}

/**
 * Take the frames of a stretch of the video, cropped and scaled to the
 * output size
 */
export async function grabFrames(
  video: HTMLVideoElement,
  crop: Crop,
  size: { width: number; height: number },
  start: number,
  seconds: number,
  onProgress: (done: number) => void,
): Promise<Frames> {
  const canvas = document.createElement("canvas");
  canvas.width = size.width;
  canvas.height = size.height;
  const context = canvas.getContext("2d", { willReadFrequently: true })!;
  context.imageSmoothingQuality = "high";

  video.pause();
  const count = Math.max(1, Math.round(seconds * BASE_FPS));
  const data: Uint8ClampedArray[] = [];
  for (let i = 0; i < count; i++) {
    await seek(video, Math.min(video.duration, start + i / BASE_FPS));
    context.drawImage(
      video,
      crop.x * video.videoWidth,
      crop.y * video.videoHeight,
      crop.width * video.videoWidth,
      crop.height * video.videoHeight,
      0,
      0,
      size.width,
      size.height,
    );
    data.push(context.getImageData(0, 0, size.width, size.height).data);
    onProgress((i + 1) / count);
  }
  return { ...size, fps: BASE_FPS, data };
}

/**
 * Encode in the worker, telling which attempt it is on
 */
export function encodeInWorker(
  frames: Frames,
  maxBytes: number,
  onTry: (index: number) => void,
  mode: "video" | "overlay" = "video",
  /** Konkord: stops the worker (the dialog was closed) */
  signal?: AbortSignal,
) {
  const worker = new Worker(new URL("./encode.worker.ts", import.meta.url), {
    type: "module",
  });
  return new Promise<{
    bytes: Uint8Array;
    plan: Plan;
    width: number;
    height: number;
    fits: boolean;
  }>((resolve, reject) => {
    worker.onmessage = (event) => {
      if (event.data.type === "try") return onTry(event.data.index);
      worker.terminate();
      resolve(event.data);
    };
    worker.onerror = (error) => {
      worker.terminate();
      reject(error);
    };
    if (signal?.aborted) {
      worker.terminate();
      return reject(signal.reason);
    }
    signal?.addEventListener(
      "abort",
      () => {
        worker.terminate();
        reject(signal.reason);
      },
      { once: true },
    );
    worker.postMessage(
      { frames, maxBytes, mode },
      frames.data.map((frame) => frame.buffer as ArrayBuffer),
    );
  });
}
